import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools as DevTools } from '@tanstack/react-query-devtools';
import { SignalRProvider } from './contexts/SignalRContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { GerarCatalogoPage } from './pages/GerarCatalogoPage';
import AgrupamentoPage from './pages/AgrupamentoPage';
import CatalogoDigitalPage from './pages/CatalogoDigitalPage';
import OrganizacaoPage from './pages/OrganizacaoPage';
import AuditoriaPage from './pages/AuditoriaPage';
import MetasCompraPage from './pages/MetasCompraPage';
import MetasCompraDimensaoPage from './pages/MetasCompraDimensaoPage';
import MetasCompraDimensaoLotePage from './pages/MetasCompraDimensaoLotePage';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AccessGate from './components/auth/AccessGate';
import SessionExpiredDialog from './components/auth/SessionExpiredDialog';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import PerfilPage from './pages/PerfilPage';
import ConfiguracoesPage from './pages/ConfiguracoesPage';
import PedidosPage from './pages/PedidosPage';
import XmlTerceirosPage from './pages/XmlTerceirosPage';
import UsuariosPage from './pages/UsuariosPage';
import CteMonitoringPage from './pages/CteMonitoringPage';
import ConferenciaListPage from './pages/ConferenciaListPage';
import ConferenciaDetailPage from './pages/ConferenciaDetailPage';
import NovaConferenciaPage from './pages/NovaConferenciaPage';
import { lazy, Suspense } from 'react';

// Página pesada (AG Grid + Recharts) — carregada sob demanda
const AnaliseCompraPage = lazy(() => import('./pages/AnaliseCompraPage'));
const SugestoesCompraPage = lazy(() => import('./pages/SugestoesCompraPage'));
const PedidosCompraPage = lazy(() => import('./pages/PedidosCompraPage'));
const ReposicaoPage = lazy(() => import('./pages/ReposicaoPage'));
const ExploradorPage = lazy(() => import('./pages/ExploradorPage'));
const FornecedorGestaoPage = lazy(() => import('./pages/FornecedorGestaoPage'));
const MarcaGestaoPage = lazy(() => import('./pages/MarcaGestaoPage'));
const CotacoesPage = lazy(() => import('./pages/CotacoesPage'));
const CotacaoDetalhePage = lazy(() => import('./pages/CotacaoDetalhePage'));
const CotacaoPublicaPage = lazy(() => import('./pages/CotacaoPublicaPage'));

// Skeleton genérico exibido enquanto o chunk lazy ainda carrega.
// Toolbar superior + grid de 4 cards cobre o layout dominante (Reposição,
// AnaliseCompra, listagens) — páginas com layout muito diferente do padrão
// veem skeleton "errado" por <500ms, aceitável.
const LazyFallback = () => (
  <div className="space-y-3 p-3 md:p-4">
    <div className="flex items-center gap-2">
      <div className="h-8 w-48 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
      <div className="h-8 w-32 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
      <div className="ml-auto h-8 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
    </div>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-32 w-full animate-pulse rounded-lg bg-zinc-200 dark:bg-zinc-800" />
      ))}
    </div>
  </div>
);

// Antes existia um AuthGuard que navegava direto pra /login em 401, fazendo o
// usuário perder qualquer trabalho em progresso (filtros, edições, etc).
// Substituído pelo SessionExpiredDialog — modal que aparece em cima da tela
// atual, pede a senha, e ao continuar o request original volta a funcionar
// sem perder estado. Ver components/auth/SessionExpiredDialog.tsx.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmDialogProvider>
        <Router>
          <SessionExpiredDialog />
          <AccessGate />
          <SignalRProvider>
            <ErrorBoundary>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              {/* Form público da cotação — sem auth, sem Layout. Acessado pelo fornecedor
                  via link compartilhado pelo comprador. Rota fora do Layout intencionalmente. */}
              <Route path="/cot/:token" element={
                <Suspense fallback={<LazyFallback />}>
                  <CotacaoPublicaPage />
                </Suspense>
              } />
              <Route path="/" element={<Layout />}>
                {/* Home (/) é grid de cards filtrado por permissão — qualquer logado vê. */}
                <Route index element={<HomePage />} />
                {/* Dashboard saiu da raiz pra /dashboard porque usuário sem permissão
                    de dashboard ficava trancado fora do sistema. */}
                <Route path="dashboard" element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                } />
                <Route path="perfil" element={<PerfilPage />} />
                {/* Configurações: livre como /perfil — Aparência é por usuário;
                    seções globais (parâmetros) só renderizam pra ADMIN. */}
                <Route path="config" element={<ConfiguracoesPage />} />
                <Route path="pedidos" element={
                  <ProtectedRoute>
                    <PedidosPage />
                  </ProtectedRoute>
                } />
                <Route path="xml-terceiros" element={
                  <ProtectedRoute>
                    <XmlTerceirosPage />
                  </ProtectedRoute>
                } />
                <Route path="gerarCatalogo" element={
                  <ProtectedRoute>
                    <GerarCatalogoPage />
                  </ProtectedRoute>
                } />
                <Route path="agrupamentos" element={
                  <ProtectedRoute>
                    <AgrupamentoPage />
                  </ProtectedRoute>
                } />
                <Route path="catalogoDigital" element={
                  <ProtectedRoute>
                    <CatalogoDigitalPage />
                  </ProtectedRoute>
                } />
                <Route path="organizacao" element={
                  <ProtectedRoute>
                    <OrganizacaoPage />
                  </ProtectedRoute>
                } />
                <Route path="auditoria" element={
                  <ProtectedRoute>
                    <AuditoriaPage />
                  </ProtectedRoute>
                } />
                <Route path="compras/metas" element={
                  <ProtectedRoute>
                    <MetasCompraPage />
                  </ProtectedRoute>
                } />
                <Route path="compras/metas/dimensao" element={
                  <ProtectedRoute>
                    <MetasCompraDimensaoPage />
                  </ProtectedRoute>
                } />
                <Route path="compras/metas/dimensao/lote" element={
                  <ProtectedRoute>
                    <MetasCompraDimensaoLotePage />
                  </ProtectedRoute>
                } />
                <Route path="cte-terceiros" element={
                  <ProtectedRoute>
                    <CteMonitoringPage />
                  </ProtectedRoute>
                } />
                <Route path="tarefas" element={
                  <ProtectedRoute>
                    <div>Tarefas Page</div>
                  </ProtectedRoute>
                } />
                <Route path="ocorrencias" element={
                  <ProtectedRoute>
                    <div>Ocorrências Page</div>
                  </ProtectedRoute>
                } />
                <Route path="relatorios" element={
                  <ProtectedRoute>
                    <div>Relatórios Page</div>
                  </ProtectedRoute>
                } />
                <Route path="usuarios" element={
                  <ProtectedRoute>
                    <UsuariosPage />
                  </ProtectedRoute>
                } />
                <Route path="conferencia" element={
                  <ProtectedRoute>
                    <ConferenciaListPage />
                  </ProtectedRoute>
                } />
                <Route path="compras/analise" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <AnaliseCompraPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/sugestoes" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <SugestoesCompraPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/pedidos" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <PedidosCompraPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/reposicao" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <ReposicaoPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="explorador" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <ExploradorPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/fornecedores" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <FornecedorGestaoPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/marcas" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <MarcaGestaoPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/cotacoes" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <CotacoesPage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/cotacoes/:empresa/:codigo" element={
                  <ProtectedRoute>
                    <Suspense fallback={<LazyFallback />}>
                      <CotacaoDetalhePage />
                    </Suspense>
                  </ProtectedRoute>
                } />
                <Route path="compras/vendas" element={<Navigate to="/compras/reposicao" replace />} />
                <Route path="conferencia/nova" element={
                  <ProtectedRoute>
                    <NovaConferenciaPage />
                  </ProtectedRoute>
                } />
                <Route path="conferencia/:empresa/:codigoId" element={
                  <ProtectedRoute>
                    <ConferenciaDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="conferencia/:empresa/:codigoId/editar" element={
                  <ProtectedRoute>
                    <ConferenciaDetailPage />
                  </ProtectedRoute>
                } />
                <Route path="config/status-ocorrencias" element={
                  <ProtectedRoute>
                    <div>Status de Ocorrências Page</div>
                  </ProtectedRoute>
                } />
                <Route path="perfil" element={
                  <ProtectedRoute>
                    <div>Perfil Page</div>
                  </ProtectedRoute>
                } />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
          </SignalRProvider>
        </Router>
          </ConfirmDialogProvider>
        </ToastProvider>
      </ThemeProvider>
      {import.meta.env.DEV && (
        <DevTools />
      )}
    </QueryClientProvider>
  );
}

export default App;
